import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { redactString, redactJson } from '../lib/phi.js';

export interface WireDetailsDrawerProps {
  sessionId: string;
  rowId: string | null;
  /**
   * When true the drawer renders unredacted URLs / headers / bodies.
   * Wired by the parent's session-scoped "Reveal PHI" toggle. Default false.
   */
  reveal?: boolean;
  onClose: () => void;
}

/**
 * Slide-out drawer rendering full wire-level details for a single raw_cache
 * row. Shared between the cache browser (per-record) and the Network tab
 * (per-request). Reads via api.getCacheRawRow which always returns the full
 * request and response payload.
 *
 * PHI redaction is applied to the URL, request body, response body, and any
 * suspicious header values (cookies, auth tokens) by default. When `reveal`
 * is true, raw values are shown — caller is responsible for the audit log.
 */
export function WireDetailsDrawer({ sessionId, rowId, reveal = false, onClose }: WireDetailsDrawerProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['cache-raw', sessionId, rowId],
    queryFn: () => (rowId ? api.getCacheRawRow(sessionId, rowId) : Promise.resolve(null)),
    enabled: !!rowId,
  });

  // Tab state
  const [tab, setTab] = useState<'overview' | 'request' | 'response' | 'body'>('overview');

  // Reset to overview tab when switching rows
  useEffect(() => { setTab('overview'); }, [rowId]);

  // Close on Escape
  useEffect(() => {
    if (!rowId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rowId, onClose]);

  if (!rowId) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" />
      <div
        className="w-[640px] max-w-[80vw] h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'rgb(15,23,42)',
          borderLeft: '1px solid rgba(148,163,184,0.25)',
          boxShadow: '-8px 0 30px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-12 border-b border-border shrink-0">
          <div className="text-sm font-semibold text-text-primary">Wire details</div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-dimmed hover:text-text-primary text-xs"
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-4 pt-3 pb-2 border-b border-border shrink-0">
          {(['overview', 'request', 'response', 'body'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className="text-xs uppercase tracking-widest font-mono px-3 py-1 rounded"
              style={{
                background: tab === t ? 'rgba(56,189,248,0.18)' : 'transparent',
                color: tab === t ? 'rgb(125,211,252)' : 'var(--text-dimmed)',
                border: tab === t ? '1px solid rgba(56,189,248,0.45)' : '1px solid transparent',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading && <div className="text-sm text-text-dimmed">Loading…</div>}
          {error && <div className="text-sm text-rose-300">Failed to load: {String(error)}</div>}
          {data && tab === 'overview' && (
            <Overview data={data} reveal={reveal} />
          )}
          {data && tab === 'request' && (
            <RequestDetails data={data} reveal={reveal} />
          )}
          {data && tab === 'response' && (
            <ResponseDetails data={data} reveal={reveal} />
          )}
          {data && tab === 'body' && (
            <BodyView data={data} reveal={reveal} />
          )}
        </div>
      </div>
    </div>
  );
}

interface RawData {
  url: string;
  method: string;
  requestHeaders: Record<string, string>;
  requestBody: string | null;
  responseStatus: number;
  responseHeaders: Record<string, string>;
  durationMs: number;
  bytes: number;
  bodyJson: unknown;
  bodyBlobPath: string | null;
  endpoint: string;
  sourceRecordId: string;
  fetchedAt: string;
}

function Overview({ data, reveal }: { data: RawData; reveal: boolean }) {
  const ok = data.responseStatus >= 200 && data.responseStatus < 400;
  const url = reveal ? data.url : redactString(data.url, 'url');
  return (
    <div className="space-y-3 text-sm">
      <Row label="Endpoint" value={data.endpoint} mono />
      <Row label="URL" value={url} mono />
      <Row label="Method" value={data.method} />
      <Row label="Status" value={`${data.responseStatus}`} valueColor={ok ? 'rgb(94,234,212)' : 'rgb(253,164,175)'} />
      <Row label="Duration" value={`${data.durationMs}ms`} />
      <Row label="Bytes" value={`${data.bytes.toLocaleString()}`} />
      <Row label="Source ID" value={reveal ? data.sourceRecordId : redactString(data.sourceRecordId, 'id')} mono />
      <Row label="Fetched at" value={new Date(data.fetchedAt).toISOString()} mono />
    </div>
  );
}

function RequestDetails({ data, reveal }: { data: RawData; reveal: boolean }) {
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">URL</div>
        <pre className="font-mono text-xs whitespace-pre-wrap break-all rounded p-2"
             style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(148,163,184,0.18)' }}>
          {data.method} {reveal ? data.url : redactString(data.url, 'url')}
        </pre>
      </div>
      <Headers title="Request headers" headers={data.requestHeaders} reveal={reveal} />
      {data.requestBody && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">Request body</div>
          <pre className="font-mono text-xs whitespace-pre-wrap break-all rounded p-2 max-h-64 overflow-y-auto"
               style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(148,163,184,0.18)' }}>
            {reveal ? data.requestBody : redactString(data.requestBody, 'body')}
          </pre>
        </div>
      )}
    </div>
  );
}

function ResponseDetails({ data, reveal }: { data: RawData; reveal: boolean }) {
  return (
    <div className="space-y-3 text-sm">
      <Row label="Status" value={String(data.responseStatus)} />
      <Headers title="Response headers" headers={data.responseHeaders} reveal={reveal} />
    </div>
  );
}

function BodyView({ data, reveal }: { data: RawData; reveal: boolean }) {
  if (data.bodyJson === null && !data.bodyBlobPath) {
    return <div className="text-sm text-text-dimmed italic">No body captured for this row.</div>;
  }
  if (data.bodyBlobPath) {
    return (
      <div className="space-y-2 text-sm">
        <div className="text-text-dimmed">Body stored in blob (large response): {data.bodyBlobPath}</div>
        <div className="text-xs text-text-dimmed italic">Inline blob fetch is not yet wired.</div>
      </div>
    );
  }
  const rendered = reveal
    ? JSON.stringify(data.bodyJson, null, 2)
    : JSON.stringify(redactJson(data.bodyJson), null, 2);
  return (
    <pre className="font-mono text-xs whitespace-pre-wrap break-all rounded p-2 max-h-[60vh] overflow-y-auto"
         style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(148,163,184,0.18)' }}>
      {rendered}
    </pre>
  );
}

function Headers({ title, headers, reveal }: { title: string; headers: Record<string, string>; reveal: boolean }) {
  const entries = Object.entries(headers);
  if (entries.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">{title}</div>
      <div className="rounded p-2 space-y-1"
           style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(148,163,184,0.18)' }}>
        {entries.map(([k, v]) => (
          <div key={k} className="font-mono text-xs flex gap-2">
            <span className="text-text-secondary shrink-0 min-w-[140px] truncate" title={k}>{k}:</span>
            <span className="text-text-primary break-all">
              {reveal ? v : redactHeader(k, v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, mono = false, valueColor }: { label: string; value: string; mono?: boolean; valueColor?: string }) {
  return (
    <div className="flex gap-3">
      <div className="text-[10px] uppercase tracking-widest text-text-secondary min-w-[80px] mt-0.5">{label}</div>
      <div
        className={`text-sm break-all flex-1 ${mono ? 'font-mono' : ''}`}
        style={{ color: valueColor ?? 'var(--text-primary)' }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Header redaction — anything that smells like a credential gets masked.
 * Cookies and authorization headers are PHI-adjacent (often scoped to a
 * specific patient session), so redact even when bodies aren't.
 */
function redactHeader(name: string, value: string): string {
  const k = name.toLowerCase();
  if (k === 'cookie' || k === 'set-cookie') return '••• [cookie redacted] •••';
  if (k === 'authorization') return '••• [token redacted] •••';
  if (k.startsWith('x-csrf') || k.includes('-token')) return '••• [token redacted] •••';
  return value;
}
